import {
  cn,
  deriveTrackName,
  omitDataAttributes,
  pickDataAttributes,
} from "@design-system/utils";
import { cva } from "class-variance-authority";
import { CloudUpload, File as FileIcon, X } from "lucide-react";
import * as React from "react";
import { useDropzone, type Accept, type FileRejection } from "react-dropzone";

import { Button } from "../button";
import { Loader } from "../loader";

const fileUploadVariants = cva(
  "focus-visible:ring-ring-default flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed px-6 py-8 text-center transition-colors outline-none focus-visible:ring-[3px]",
  {
    variants: {
      state: {
        idle: "border-shape-neutral-outlined-border-default bg-shape-neutral-outlined-background text-foreground-neutral-muted hover:border-shape-neutral-outlined-border-emphasised hover:bg-shape-neutral-outlined-background-hover cursor-pointer",
        active:
          "border-shape-active-outlined-border-emphasised bg-shape-active-outlined-background-hover text-shape-active-outlined-foreground-default cursor-copy",
        reject:
          "border-shape-critical-outlined-border-emphasised bg-shape-critical-outlined-background text-shape-critical-outlined-foreground-default cursor-no-drop",
        disabled:
          "border-shape-neutral-outlined-border-default bg-shape-neutral-outlined-background text-foreground-neutral-muted cursor-not-allowed opacity-50",
      },
    },
    defaultVariants: {
      state: "idle",
    },
  }
);

export interface FileUploadValue {
  /** File name shown next to the preview. */
  name: string;
  /** Preview URL; when present an image preview is rendered, otherwise a file icon. */
  url?: string;
  /** MIME type; decides between an image preview and the generic file icon. */
  type?: string;
}

export interface FileUploadProps extends Omit<
  React.ComponentProps<"div">,
  "onSelect" | "onChange" | "children"
> {
  /** Accessible name for the file input — screen readers and role/label queries. */
  label: string;
  /** Primary prompt in the drop area, e.g. "Click to upload or drag and drop". */
  prompt: React.ReactNode;
  /** Secondary hint, typically the accepted formats, e.g. "SVG, PNG, JPG or GIF". */
  hint?: React.ReactNode;
  /**
   * Message shown, in an `alert` live region, when a selected file is rejected.
   * Required so a rejection is never signalled silently. Consumer-supplied copy.
   */
  rejectionMessage: React.ReactNode;
  /** Accepted file types in react-dropzone's `Accept` shape, e.g. `{ "image/png": [] }`. */
  accept?: Accept;
  /** Largest accepted file size in bytes. */
  maxSize?: number;
  /** Smallest accepted file size in bytes. */
  minSize?: number;
  /** Invoked with the accepted file when the user browses or drops one. */
  onSelect: (file: File) => void;
  /** Consumer-supplied current file to preview. */
  value?: FileUploadValue | null;
  /** Accessible label for the clear control; when omitted the value cannot be cleared. */
  clearLabel?: string;
  /** Invoked when the user clears the current value. */
  onClear?: () => void;
  /** While set, selections are blocked and the busy state is shown alongside the value. */
  busy?: boolean;
  /**
   * Accessible label and text for the busy indicator. Required — no bundled copy;
   * the consumer supplies its own localized string.
   */
  busyLabel: string;
  /** Disables the input without showing the busy state. */
  disabled?: boolean;
}

function FileUpload({
  label,
  prompt,
  hint,
  rejectionMessage,
  accept,
  maxSize,
  minSize,
  onSelect,
  value,
  clearLabel,
  onClear,
  busy = false,
  busyLabel,
  disabled = false,
  className,
  ...props
}: FileUploadProps) {
  const [isRejected, setIsRejected] = React.useState(false);
  const isDisabled = disabled || busy;

  const handleDropAccepted = React.useCallback(
    (files: File[]) => {
      const [file] = files;
      if (!file) return;
      setIsRejected(false);
      onSelect(file);
    },
    [onSelect]
  );

  const handleDropRejected = React.useCallback(
    (rejections: FileRejection[]) => {
      if (rejections.length > 0) setIsRejected(true);
    },
    []
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      accept,
      maxSize,
      minSize,
      multiple: false,
      disabled: isDisabled,
      onDropAccepted: handleDropAccepted,
      onDropRejected: handleDropRejected,
    });

  const state = isDisabled
    ? "disabled"
    : isDragReject
      ? "reject"
      : isDragAccept
        ? "active"
        : "idle";

  const isImageValue = value?.url && (value.type?.startsWith("image/") ?? true);
  const canClear = Boolean(value && !busy && clearLabel && onClear);
  const dataAttributes = pickDataAttributes(props);

  return (
    <div
      data-slot="file-upload"
      className={cn("flex flex-col gap-3", className)}
      {...omitDataAttributes(props)}
    >
      <div
        {...getRootProps({
          ...dataAttributes,
          "data-slot": "file-upload-dropzone",
          className: fileUploadVariants({ state }),
        })}
        aria-invalid={isRejected || undefined}
        aria-disabled={isDisabled || undefined}
      >
        {/* sr-only keeps the input in the a11y tree (labelled, keyboard/SR
            reachable) while overriding react-dropzone's injected inline style. */}
        <input
          {...getInputProps({
            "aria-label": label,
            disabled: isDisabled,
            className: "sr-only",
            style: undefined,
          })}
        />
        <CloudUpload aria-hidden className="pointer-events-none size-6" />
        <div className="text-foreground-neutral-default pointer-events-none text-sm font-medium">
          {prompt}
        </div>
        {hint && (
          <div className="text-foreground-neutral-muted pointer-events-none text-xs">
            {hint}
          </div>
        )}
      </div>

      {isRejected && (
        <p
          role="alert"
          className="text-shape-critical-outlined-foreground-default text-xs"
        >
          {rejectionMessage}
        </p>
      )}

      {(value || busy) && (
        <div className="border-shape-neutral-outlined-border-default flex items-center gap-3 rounded-md border p-2">
          {value && (
            <>
              {isImageValue ? (
                <img
                  src={value.url}
                  alt={value.name}
                  className="size-10 shrink-0 rounded object-contain"
                />
              ) : (
                <FileIcon
                  aria-hidden
                  className="text-foreground-neutral-muted size-10 shrink-0"
                />
              )}
              <span className="text-foreground-neutral-default min-w-0 flex-1 truncate text-sm">
                {value.name}
              </span>
            </>
          )}

          {busy ? (
            <div
              aria-live="polite"
              className="text-foreground-neutral-muted ml-auto flex shrink-0 items-center gap-2 text-sm"
            >
              <Loader size={16} aria-label={busyLabel} />
              <span>{busyLabel}</span>
            </div>
          ) : (
            canClear && (
              <Button
                type="button"
                variant="ghost"
                size="smaller-icon"
                onClick={onClear}
                aria-label={clearLabel}
                className="ml-auto shrink-0"
                {...deriveTrackName(dataAttributes, "-clear")}
              >
                <X aria-hidden />
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export { FileUpload, fileUploadVariants };
